import { useState, useEffect, useCallback } from 'react';
import { useAnimation } from 'framer-motion';
import { ANIMATION_POSITIONS } from '../constants/animations';

/**
 * Custom hook for handling scroll-based animations with debouncing and performance optimizations
 * @returns {Object} Animation controls for the share button
 */
export const useScrollAnimation = () => {
  // Track the last scroll position to calculate scroll direction and delta
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Flag to prevent rapid state changes during smooth scrolls
  // This helps eliminate flickering during continuous scrolling
  const [isScrolling, setIsScrolling] = useState(false);
  
  // Framer Motion animation controls
  const controls = useAnimation();

  /**
   * Handles scroll events with debouncing and direction detection
   * Uses useCallback to prevent unnecessary re-renders
   */
  const handleScroll = useCallback(() => {
    const currentScrollY = window.scrollY;
    // Calculate the distance scrolled since last check
    const scrollDelta = currentScrollY - lastScrollY;
    
    // Only trigger animation if we've scrolled more than 5 pixels
    // This threshold prevents micro-movements from triggering animations
    if (Math.abs(scrollDelta) > 5) {
      // Determine scroll direction based on delta
      const direction = scrollDelta > 0 ? "down" : "up";
      
      // Prevent rapid state changes during smooth scroll
      // This is crucial for preventing flickering during continuous scrolling
      if (!isScrolling) {
        setIsScrolling(true);
        
        // Start appropriate animation based on scroll direction
        if (direction === "down") {
          controls.start(ANIMATION_POSITIONS.hidden);
        } else {
          controls.start(ANIMATION_POSITIONS.visible);
        }
        
        // Reset scrolling state after animation completes
        // The timeout duration (150ms) is slightly longer than the animation duration
        // to ensure smooth transitions between scroll events
        setTimeout(() => {
          setIsScrolling(false);
        }, 150);
      }
      
      // Update last scroll position for next comparison
      setLastScrollY(currentScrollY);
    }
  }, [lastScrollY, controls, isScrolling]);

  useEffect(() => {
    // Use requestAnimationFrame for better performance
    // This helps prevent jank during scroll events
    let ticking = false;
    
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Add scroll event listener with passive flag for better performance
    // The passive flag tells the browser that the event handler won't call preventDefault()
    window.addEventListener("scroll", onScroll, { passive: true });
    
    // Cleanup: remove event listener when component unmounts
    return () => window.removeEventListener("scroll", onScroll);
  }, [handleScroll]);

  return { controls };
}; 